// repo/primary-care-connect/frontend/src/pages/Portal/CommunityNavigator/CaseDetail/intakeSummary.ts

import { emptyIntakeForm } from "./intakeDefaults";
import {
  familyDoctorSituationOptions,
  priorityOverrideReasonOptions,
} from "./intakeOptions";
import type {
  GeneratedIntakeSummary,
  IntakeFormState,
  NavigatorCaseDetail,
} from "./types";
import {
  compactList,
  getChoiceLabel,
  getNestedRecord,
  getPatientAgeYears,
  isRecord,
  readBoolean,
  readString,
} from "./intakeUtils";

function asPositiveNumber(value: string): number | null {
  const n = Number.parseInt(String(value || ""), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function formatPreferredDoctorGender(value: string): string | null {
  if (!value || value === "NoPreference") return null;
  return value;
}

export function buildHydratedIntakeForm(
  caseDetail: NavigatorCaseDetail
): IntakeFormState {
  if (isRecord(caseDetail.intakeDraft)) {
    return buildInitialIntakeForm({
      ...caseDetail,
      intakeAnswers: caseDetail.intakeDraft,
      preferredLanguageId: null,
    });
  }

  return buildInitialIntakeForm(caseDetail);
}

export function buildInitialIntakeForm(
  caseDetail: NavigatorCaseDetail
): IntakeFormState {
  const intakeRecord = isRecord(caseDetail.intakeAnswers)
    ? caseDetail.intakeAnswers
    : {};

  const standardRecord = getNestedRecord(
    intakeRecord,
    "standardRequestInformation"
  );
  const matchingPreferencesRecord = getNestedRecord(
    intakeRecord,
    "navigatorMatchingPreferences"
  );
  const interviewRecord = getNestedRecord(intakeRecord, "navigatorInterview");
  const hccRecord = getNestedRecord(interviewRecord, "healthCareConnect");
  const hospitalRecord = getNestedRecord(
    interviewRecord,
    "recentHospitalDischarge"
  );
  const pregnancyRecord = getNestedRecord(
    interviewRecord,
    "pregnancyNewbornMotherNewborn"
  );
  const fitRecord = getNestedRecord(interviewRecord, "fitColorectalFollowUp");
  const supportRecord = getNestedRecord(interviewRecord, "supportNeeds");
  const communicationRecord = getNestedRecord(
    interviewRecord,
    "practicalAccessAndCommunication"
  );
  const priorityRecord = getNestedRecord(intakeRecord, "priorityOverride");

  return {
    ...emptyIntakeForm,
    requestFor: readString(standardRecord, "requestFor"),
    familyDoctorSituation: readString(standardRecord, "familyDoctorSituation"),
    otherReasonText: readString(standardRecord, "otherReasonText"),
    coverageType:
      readString(standardRecord, "coverageTypeId") ||
      readString(standardRecord, "coverageType"),
    consentAccepted: readString(standardRecord, "consentAccepted"),

    preferredDoctorGender: readString(
      matchingPreferencesRecord,
      "preferredDoctorGender"
    ),
    preferredLanguageId:
      readString(matchingPreferencesRecord, "preferredLanguageId") ||
      (caseDetail.preferredLanguageId
        ? String(caseDetail.preferredLanguageId)
        : ""),

    hccRegistered:
      readString(hccRecord, "registered") ||
      readString(intakeRecord, "hccRegistered"),
    hccReferred:
      readString(hccRecord, "referred") ||
      readString(intakeRecord, "hccReferred"),
    hccComplexVulnerable:
      readString(hccRecord, "complexVulnerable") ||
      readString(intakeRecord, "hccComplexVulnerable"),
    hccReferenceInfo:
      readString(hccRecord, "hasReferenceInfo") ||
      readString(intakeRecord, "hccReferenceInfo"),
    hccReferenceNote: readString(hccRecord, "referenceNote"),
    hccReferralDate: readString(hccRecord, "referralOrContactDate"),

    hospitalOvernightLast3Months:
      readString(hospitalRecord, "overnightAdmissionLast3Months") ||
      (readBoolean(intakeRecord, "recentHospitalAdmission") ? "yes" : ""),
    dischargedWithoutFamilyDoctor: readString(
      hospitalRecord,
      "dischargedWithoutFamilyDoctor"
    ),
    dischargeDate: readString(hospitalRecord, "dischargeDate"),
    dischargeDateUnknown: readBoolean(hospitalRecord, "dischargeDateUnknown"),

    currentlyPregnant:
      readString(pregnancyRecord, "currentlyPregnant") ||
      (readBoolean(intakeRecord, "pregnancy") ? "yes" : ""),
    pregnancy30WeeksOrMore: readString(
      pregnancyRecord,
      "pregnancy30WeeksOrMore"
    ),
    pregnantHasFamilyDoctor: readString(
      pregnancyRecord,
      "pregnantHasFamilyDoctor"
    ),
    babyLast2Weeks: readString(pregnancyRecord, "babyLast2Weeks"),
    newbornHasFamilyDoctor: readString(
      pregnancyRecord,
      "newbornHasFamilyDoctor"
    ),
    multipleBirth: readString(pregnancyRecord, "multipleBirth"),
    newbornNicuLast3Months: readString(
      pregnancyRecord,
      "newbornNicuLast3Months"
    ),

    fitPositiveOrCrcFollowUp: readString(
      fitRecord,
      "fitPositiveOrCrcFollowUp"
    ),
    hasDoctorArrangingCrcFollowUp: readString(
      fitRecord,
      "hasDoctorArrangingCrcFollowUp"
    ),

    regularMedications3Plus:
      readString(supportRecord, "regularMedications3Plus") ||
      (readBoolean(intakeRecord, "chronicMedications3Plus") ? "yes" : ""),
    ongoingConditionsRegularFollowUp: readString(
      supportRecord,
      "ongoingConditionsRegularFollowUp"
    ),
    troubleManagingCare: readString(supportRecord, "troubleManagingCare"),
    mentalHealthAddictionStressSupport: readString(
      supportRecord,
      "mentalHealthAddictionStressSupport"
    ),
    mobilityDisabilityAccessibilitySupport:
      readString(supportRecord, "mobilityDisabilityAccessibilitySupport") ||
      (readBoolean(intakeRecord, "accessibilitySupportNeeded") ? "yes" : ""),
    helpCommunicatingEnglish:
      readString(supportRecord, "helpCommunicatingEnglish") ||
      (readBoolean(intakeRecord, "languageSupportNeeded") ? "yes" : ""),

    phoneBestContact: readString(communicationRecord, "phoneBestContact"),
    reliableEmailAccess:
      readString(communicationRecord, "reliableEmailAccess") ||
      (caseDetail.patient.manualNoEmail ||
      readBoolean(intakeRecord, "noEmailAccess")
        ? "no"
        : ""),
    helpBookingOrAttending: readString(
      communicationRecord,
      "helpBookingOrAttending"
    ),

    priorityOverrideLevel: readString(priorityRecord, "level"),
    priorityOverrideReason: readString(priorityRecord, "reason"),
    priorityOverrideNote: readString(priorityRecord, "note"),

    navigatorSummary: readString(intakeRecord, "navigatorSummary"),
    // Draft Save Progress stores Review-step note as top-level `note` on the draft object.
    // Completed IntakeAnswersJson does not carry this field (completion note goes to activity).
    note: readString(intakeRecord, "note"),
  };
}

export function buildGeneratedIntakeSummary(
  form: IntakeFormState,
  caseDetail: NavigatorCaseDetail | null
): GeneratedIntakeSummary {
  const age = getPatientAgeYears(caseDetail?.patient.dateOfBirth);
  const isAge65Plus = age !== null && age >= 65;
  const isAge75Plus = age !== null && age >= 75;
  const isInfantUnderOne = age !== null && age < 1;

  const isHccRegistered = form.hccRegistered === "yes";
  const isFitFollowUp = form.fitPositiveOrCrcFollowUp === "yes";

  const hccReferred =
    isHccRegistered && form.hccReferred === "yes";
  const hccComplexVulnerable =
    isHccRegistered && form.hccComplexVulnerable === "yes";
  const hccReferenceInfo =
    isHccRegistered && form.hccReferenceInfo === "yes";

  const hccFlags = compactList([
    isHccRegistered && "Health Care Connect patient reported",
    hccReferred && "Referred from Health Care Connect",
    hccComplexVulnerable && "HCC complex / vulnerable reported",
    hccReferenceInfo && "HCC reference information available",
  ]);

  const hospitalDischargeFlag =
    form.hospitalOvernightLast3Months === "yes" &&
    form.dischargedWithoutFamilyDoctor === "yes";

  const pregnantThirtyWeeksFlag =
    form.currentlyPregnant === "yes" &&
    form.pregnancy30WeeksOrMore === "yes" &&
    form.pregnantHasFamilyDoctor === "no";

  const motherNewbornFlag =
    form.currentlyPregnant === "no" &&
    form.babyLast2Weeks === "yes" &&
    form.newbornHasFamilyDoctor === "no";

  const multipleBirthFlag = ["twins", "tripletsOrMore"].includes(
    form.multipleBirth
  );

  const newbornNicuFlag =
    form.currentlyPregnant === "no" && form.newbornNicuLast3Months === "yes";

  const fitCrcFlag =
    isFitFollowUp && form.hasDoctorArrangingCrcFollowUp === "no";

  const specialPopulationFlags = compactList([
    isAge65Plus && "Age 65+ derived from DOB",
    isAge75Plus && "Age 75+ derived from DOB",
    isInfantUnderOne && "Infant under 1 derived from DOB",
    hccReferred && "Possible HCC referral context",
    hccComplexVulnerable && "Possible HCC complex / vulnerable context",
    hospitalDischargeFlag &&
      "Possible recent hospital discharge without family doctor",
    pregnantThirtyWeeksFlag &&
      "Possible pregnancy 30+ weeks without family doctor",
    motherNewbornFlag && "Possible mother/newborn unattached context",
    multipleBirthFlag && "Possible multiple birth newborn context",
    newbornNicuFlag && "Possible recent NICU newborn context",
    fitCrcFlag && "Possible FIT/CRC follow-up context",
  ]);

  const chronicDiseaseSignal =
    form.regularMedications3Plus === "yes" ||
    form.ongoingConditionsRegularFollowUp === "yes";

  const frailSeniorSignal =
    isAge75Plus ||
    (isAge65Plus &&
      (form.troubleManagingCare === "yes" ||
        form.hospitalOvernightLast3Months === "yes"));

  const newbornInfantSignal = isInfantUnderOne || form.babyLast2Weeks === "yes";

  const pregnancyPostpartumSignal =
    form.currentlyPregnant === "yes" || form.babyLast2Weeks === "yes";

  const clinicalSupportGroups = compactList([
    chronicDiseaseSignal && "Chronic disease / multiple medications",
    frailSeniorSignal && "Frail senior",
    form.mentalHealthAddictionStressSupport === "yes" &&
      "Mental health support",
    newbornInfantSignal && "Newborn / infant care",
    pregnancyPostpartumSignal && "Pregnancy / postpartum",
    form.mobilityDisabilityAccessibilitySupport === "yes" &&
      "Disability / accessibility support needs",
  ]);

  const complexSignals = [
    hccComplexVulnerable,
    hospitalDischargeFlag,
    form.troubleManagingCare === "yes" && chronicDiseaseSignal,
    form.mentalHealthAddictionStressSupport === "yes" &&
      form.troubleManagingCare === "yes",
  ].filter(Boolean).length;

  const moderateSignals = [
    chronicDiseaseSignal,
    form.hospitalOvernightLast3Months === "yes",
    form.mobilityDisabilityAccessibilitySupport === "yes",
    form.helpCommunicatingEnglish === "yes",
    form.reliableEmailAccess === "no",
    form.helpBookingOrAttending === "yes",
  ].filter(Boolean).length;

  let estimatedSupportLevel = "Standard primary care need";

  if (complexSignals >= 2) {
    estimatedSupportLevel = "Complex medical or social need";
  } else if (complexSignals === 1 || moderateSignals >= 2) {
    estimatedSupportLevel = "Moderate support need";
  }

  if (
    hccComplexVulnerable &&
    form.troubleManagingCare === "yes" &&
    form.helpBookingOrAttending === "yes"
  ) {
    estimatedSupportLevel = "Complex case — discuss before forwarding";
  }

  const preferredDoctorGender = formatPreferredDoctorGender(
    form.preferredDoctorGender
  );

  const matchingSignals = compactList([
    `Estimated support level: ${estimatedSupportLevel}`,
    ...clinicalSupportGroups,
    preferredDoctorGender &&
      `Preferred doctor gender: ${preferredDoctorGender}`,
    form.preferredLanguageId && "Preferred doctor language selected",
    form.helpCommunicatingEnglish === "yes" && "Language support needed",
    form.mobilityDisabilityAccessibilitySupport === "yes" &&
      "Accessibility support needed",
    form.reliableEmailAccess === "no" && "No reliable email access",
    form.phoneBestContact === "yes" && "Phone preferred for contact",
  ]);

  let calculatedPriority = "Routine";

  if (
    hospitalDischargeFlag ||
    pregnantThirtyWeeksFlag ||
    motherNewbornFlag ||
    fitCrcFlag
  ) {
    calculatedPriority = "High";
  } else if (
    hccComplexVulnerable ||
    hccReferred ||
    isAge75Plus ||
    newbornNicuFlag
  ) {
    calculatedPriority = "High";
  } else if (
    estimatedSupportLevel !== "Standard primary care need" ||
    form.reliableEmailAccess === "no" ||
    form.helpBookingOrAttending === "yes"
  ) {
    calculatedPriority = "Medium";
  }

  if (
    (hospitalDischargeFlag && form.dischargeDate !== "") ||
    (fitCrcFlag && form.helpBookingOrAttending === "yes")
  ) {
    calculatedPriority = "Urgent";
  }

  const priorityReasons = compactList([
    hccReferred && "Health Care Connect referral context",
    hccComplexVulnerable && "HCC complex / vulnerable reported",
    hospitalDischargeFlag && "Recent hospital discharge without family doctor",
    pregnantThirtyWeeksFlag && "Pregnancy 30+ weeks without family doctor",
    motherNewbornFlag && "Mother/newborn unattached context",
    newbornNicuFlag && "Recent NICU newborn context",
    fitCrcFlag && "FIT/CRC follow-up without family doctor arranging care",
    isAge75Plus && "Age 75+ derived from DOB",
    isInfantUnderOne && "Infant under 1 derived from DOB",
    form.reliableEmailAccess === "no" && "No reliable email access",
    form.helpBookingOrAttending === "yes" &&
      "Needs help booking or attending first appointment",
    form.troubleManagingCare === "yes" &&
      "Difficulty managing appointments, forms, or follow-up care",
  ]);

  const hasPriorityOverride =
    Boolean(form.priorityOverrideLevel) && Boolean(form.priorityOverrideReason);

  const priorityLevel = hasPriorityOverride
    ? form.priorityOverrideLevel
    : calculatedPriority;

  if (hasPriorityOverride) {
    priorityReasons.push(
      `Navigator priority override: ${getChoiceLabel(
        priorityOverrideReasonOptions,
        form.priorityOverrideReason
      )}`
    );
  }

  return {
    priorityLevel,
    priorityReasons,
    hccFlags,
    specialPopulationFlags,
    matchingSignals,
    estimatedSupportLevel,
    clinicalSupportGroups,
  };
}

export function buildAccessibilityNeeds(form: IntakeFormState) {
  return {
    accessibilitySupportNeeded:
      form.mobilityDisabilityAccessibilitySupport === "yes",
    mobilityTransportBarrier:
      form.troubleManagingCare === "yes" ||
      form.helpBookingOrAttending === "yes",
    languageSupportNeeded: form.helpCommunicatingEnglish === "yes",
  };
}

/**
 * Build raw structured intake answers for the intake-save API.
 * Does NOT include systemGenerated — the backend owns derived intelligence.
 */
export function buildIntakeAnswers(form: IntakeFormState) {
  const coverageTypeId = asPositiveNumber(form.coverageType);
  const preferredLanguageId = asPositiveNumber(form.preferredLanguageId);
  const isHccRegistered = form.hccRegistered === "yes";
  const isHccReferenceInfo =
    isHccRegistered && form.hccReferenceInfo === "yes";
  const isFitFollowUp = form.fitPositiveOrCrcFollowUp === "yes";

  return {
    navigatorInterviewVersion: "v5",
    standardRequestInformation: {
      requestFor: form.requestFor || null,
      familyDoctorSituation: form.familyDoctorSituation || null,
      familyDoctorSituationLabel: getChoiceLabel(
        familyDoctorSituationOptions,
        form.familyDoctorSituation
      ),
      otherReasonText: form.otherReasonText.trim() || null,
      coverageTypeId,
      coverageType: form.coverageType.trim() || null,
      consentAccepted: form.consentAccepted || null,
    },
    navigatorMatchingPreferences: {
      preferredDoctorGender: form.preferredDoctorGender || null,
      preferredLanguageId,
    },
    navigatorInterview: {
      healthCareConnect: {
        registered: form.hccRegistered || null,
        referred: isHccRegistered ? form.hccReferred || null : null,
        complexVulnerable: isHccRegistered
          ? form.hccComplexVulnerable || null
          : null,
        hasReferenceInfo: isHccRegistered
          ? form.hccReferenceInfo || null
          : null,
        referenceNote: isHccReferenceInfo
          ? form.hccReferenceNote.trim() || null
          : null,
        referralOrContactDate: isHccReferenceInfo
          ? form.hccReferralDate || null
          : null,
      },
      recentHospitalDischarge: {
        overnightAdmissionLast3Months:
          form.hospitalOvernightLast3Months || null,
        dischargedWithoutFamilyDoctor:
          form.dischargedWithoutFamilyDoctor || null,
        dischargeDate: form.dischargeDateUnknown
          ? null
          : form.dischargeDate || null,
        dischargeDateUnknown: form.dischargeDateUnknown,
      },
      pregnancyNewbornMotherNewborn: {
        currentlyPregnant: form.currentlyPregnant || null,
        pregnancy30WeeksOrMore:
          form.currentlyPregnant === "yes"
            ? form.pregnancy30WeeksOrMore || null
            : null,
        pregnantHasFamilyDoctor:
          form.currentlyPregnant === "yes"
            ? form.pregnantHasFamilyDoctor || null
            : null,
        babyLast2Weeks:
          form.currentlyPregnant === "no" ? form.babyLast2Weeks || null : null,
        newbornHasFamilyDoctor:
          form.currentlyPregnant === "no" && form.babyLast2Weeks === "yes"
            ? form.newbornHasFamilyDoctor || null
            : null,
        multipleBirth:
          form.currentlyPregnant === "no" && form.babyLast2Weeks === "yes"
            ? form.multipleBirth || null
            : null,
        newbornNicuLast3Months:
          form.currentlyPregnant === "no" && form.babyLast2Weeks === "yes"
            ? form.newbornNicuLast3Months || null
            : null,
      },
      fitColorectalFollowUp: {
        fitPositiveOrCrcFollowUp: form.fitPositiveOrCrcFollowUp || null,
        hasDoctorArrangingCrcFollowUp: isFitFollowUp
          ? form.hasDoctorArrangingCrcFollowUp || null
          : null,
      },
      supportNeeds: {
        regularMedications3Plus: form.regularMedications3Plus || null,
        ongoingConditionsRegularFollowUp:
          form.ongoingConditionsRegularFollowUp || null,
        troubleManagingCare: form.troubleManagingCare || null,
        mentalHealthAddictionStressSupport:
          form.mentalHealthAddictionStressSupport || null,
        mobilityDisabilityAccessibilitySupport:
          form.mobilityDisabilityAccessibilitySupport || null,
        helpCommunicatingEnglish: form.helpCommunicatingEnglish || null,
      },
      practicalAccessAndCommunication: {
        phoneBestContact: form.phoneBestContact || null,
        reliableEmailAccess: form.reliableEmailAccess || null,
        helpBookingOrAttending: form.helpBookingOrAttending || null,
      },
    },
    priorityOverride: {
      level: form.priorityOverrideLevel || null,
      reason: form.priorityOverrideReason || null,
      reasonLabel: getChoiceLabel(
        priorityOverrideReasonOptions,
        form.priorityOverrideReason
      ),
      note: form.priorityOverrideNote.trim() || null,
    },
    navigatorSummary: form.navigatorSummary.trim() || null,
  };
}